import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { ColWithGap, RowWithGap } from '@components/styles';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import ReservationCheckResult from '@components/ringil3/Features/Reservation/Helper/ReservationCheckResult';
import useForm from '@hooks/useForm/useForm';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import {
  CompanyModuleEnum,
  ComputeReservationDurationConfiguration,
  FreeSlotsWrapperOutDTO,
  MessageResponse,
  TimeslotCalendarDTO,
  CompanyLocationRampWithOpeningHoursOutDTO,
} from '@api/ringil-api.v2';
import { Order } from '@components/framework/orders/common';
import { useExternalOrdersDateRange } from '@components/ringil3/Features/Reservation/Helper/useExternalOrdersDateRange';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue';
import { ClickableCursor, HorizontalLine, TwoColGrid } from '@components/ringil3/styles';
import { findValidationMessage } from '@utils/validation';
import InputCompanyRamp from '@components/ringil3/Input/InputCompanyRamp/InputCompanyRamp';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix';
import InputHourManual from '@components/ringil3/Input/InputHour/InputHourManual';
import { deepEqual, isNotNullOrUndefined, parseNumberOrUndefined } from '@utils/utils';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon';
import SvgAvailableDates from '@components/ringil3/icons/reservation/AvailableDates';
import Panel from '@components/ringil3/Panel/Panel';
import SvgClose from '@components/ringil3/icons/panel/Close';
import COSelect from '@components/ringil3/COSelect/COSelect';
import { stringHHmmFromStringInTz, stringYMDFromDate } from '@utils/dateWithTz';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';
import SvgRefresh from '@components/ringil3/icons/Refresh';
import { ProgressSpinner } from 'primereact/progressspinner';
import InputCompanyLocation from '@components/obsolete/Form/InputEntityOwner/InputCompanyLocation.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilCalendar from '@components/framework/input/RingilCalendar';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import NoAvailableSlotsMessage from '@components/RingilComponents/NoAvailableSlots/NoAvailableSlotsMessage.tsx';
import {
  EditReservationSlotForm as Form,
  editReservationSlotFormFromDetail,
  editSlotTimeslotForReservationCheck,
  reservationOrdersFromDetail,
  updateReservationTimeslotRequest,
} from '@components/ringil3/Features/Reservation/Sidedbars/editReservationSlotMapping.ts';

interface ComponentProps {
  reservationId: string;
  applicationId: string;
  companyLocationCustomerId: string;
  onRefresh: () => void;
  expeditionLocationId: string;
}

export const useCreateEditReservationSlotSidebar = () => {
  const [visible, setVisible] = useState(false);

  const EditReservationSlotSidebar: React.FC<ComponentProps> = ({
                                                                  reservationId,
                                                                  applicationId,
                                                                  companyLocationCustomerId,
                                                                  onRefresh,
                                                                  expeditionLocationId,
                                                                }) => {
    const { tr } = useTranslationLgs();
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [apiRequest, setApiRequest] = useState(false);
    const {
      getReservationDetail,
      updateReservationTimeslot,
      checkReservation,
      getFreeSlotsForLocationReservation,
      getCreateEditReservationSettings,
      getSharedRampsForCompanyLocation,
    } = useContext(ApiContext);
    const [slots, setSlots] = useState<FreeSlotsWrapperOutDTO[]>();
    const [ramps, setRamps] = useState<CompanyLocationRampWithOpeningHoursOutDTO[]>([]);
    const [originalValues, setOriginalValues] = useState<Omit<Form, 'companyLocationId'>>();
    const [settings, setSettings] = useState<ComputeReservationDurationConfiguration | null>(null);
    const [reservationCompanyOwnerId, setReservationCompanyOwnerId] = useState<string>();
    // RD-2869: the expedition Location the reservation is linked to - the expeditionLocationId prop carries
    // the company location id from both mounters, so the value loaded from the reservation detail is authoritative
    const [linkedExpeditionLocationId, setLinkedExpeditionLocationId] = useState<string | null>(null);
    const [orders, setOrders] = useState<Order[]>([]);
    const { loggedUser } = useContext(AuthenticatedUserContext);

    useEffect(() => {
      getCreateEditReservationSettings(reservationCompanyOwnerId, d => {
        setSettings(d);
      });
    }, [reservationCompanyOwnerId]);

    useEffect(() => {
      const ac = new AbortController();

      getReservationDetail(
        reservationId,
        d => {
          if (ac.signal.aborted) return;

          setReservationCompanyOwnerId(d.customerId);
          setLinkedExpeditionLocationId(d.linkedToExpeditionLocationId ?? null);
          setOrders(reservationOrdersFromDetail(d));

          const { formValues, originalValues } = editReservationSlotFormFromDetail(d);
          form.overwriteValues(formValues);
          setOriginalValues(originalValues);
        },
        null,
        { onValidationFailed: setBeValidationErrors },
      );
      return () => {
        ac.abort();
      };
    }, [reservationId]);

    const form = useForm<Form>(
      yup.object().shape({
        rampId: yup.string().required(),
        durationInMin: yup.number().required(),
        localDate: yup.string().required(),
        companyLocationId: yup.string().required(),
        arrivalHourString: yup.string().required(),
      }),
      null,
      d => save(d),
      false,
      false,
      null,
      'scroll',
    );

    const [saving, setSaving] = useState(false);

    const save = (data: Form) => {
      console.log('Saving reservation with timezoneId:', data.timezoneId);
      updateReservationTimeslot(
        reservationId,
        updateReservationTimeslotRequest(data),
        () => {
          setSaving(false);
          setVisible(false);
          onRefresh();
        },
        null,
        {
          onValidationFailed: v => {
            setSaving(false);
            setBeValidationErrors(v);
          },
          onError: (_error, showStandardError) => {
            setSaving(false);
            showStandardError();
          },
        },
      );
    };

    const offerAvailableSlots = () => {
      if (form.values.localDate && form.values.durationInMin && form.values.companyLocationId) {
        // RD-2869: the reservation variant of the endpoint restricts the offer to the linked expedition's opening hours
        getFreeSlotsForLocationReservation(
          form.values.localDate,
          parseInt(form.values.durationInMin),
          form.values.companyLocationId,
          [reservationId],
          {
            customerId: companyLocationCustomerId,
            rampId: null,
            locationId: form.values.companyLocationId,
            labelIds: [],
            linkToExpeditionLocationId: linkedExpeditionLocationId,
            openGate: false,
            createExpedition: false,
            sendNotification: true,
          },
          d => {
            setSlots(d);
          },
        );
      }
    };

    useEffect(() => {
      if (!form?.values) return;
      offerAvailableSlots();
    }, [form?.values?.localDate, form?.values?.durationInMin, form?.values?.companyLocationId, linkedExpeditionLocationId]);

    // Load ramps when companyLocationId changes (needed for ramp picker and timezone)
    useEffect(() => {
      const ac = new AbortController();
      if (isNotNullOrUndefined(form.values?.companyLocationId) && isNotNullOrUndefined(companyLocationCustomerId)) {
        getSharedRampsForCompanyLocation(
          form.values.companyLocationId,
          companyLocationCustomerId,
          false,
          (loadedRamps) => {
            if (!ac.signal.aborted) {
              setRamps(loadedRamps);
            }
          }
        );
      } else {
        setRamps([]);
      }
      return () => {
        ac.abort();
      };
    }, [form.values?.companyLocationId, companyLocationCustomerId]);

    // Update timezoneId when rampId changes (ramp has priority over location)
    useEffect(() => {
      if (isNotNullOrUndefined(form.values?.rampId) && ramps.length > 0) {
        const selectedRamp = ramps.find(r => r.rampId === form.values.rampId);
        if (selectedRamp?.timezoneId && selectedRamp.timezoneId !== form.values.timezoneId) {
          console.log('Updating timezoneId from selected ramp:', selectedRamp.timezoneId);
          form.setFieldValue('timezoneId', selectedRamp.timezoneId);
        }
      }
    }, [form.values?.rampId, ramps]);

    // Set timezoneId from location's first ramp only if not set (fallback for null from DTO)
    useEffect(() => {
      if (!form.values?.rampId && !form.values?.timezoneId && ramps.length > 0) {
        const firstRampTimezone = ramps[0]?.timezoneId;
        if (firstRampTimezone) {
          console.log('Setting timezoneId from location first ramp (fallback):', firstRampTimezone);
          form.setFieldValue('timezoneId', firstRampTimezone);
        }
      }
    }, [ramps, form.values?.rampId, form.values?.timezoneId]);

    const applySlot = (slot: TimeslotCalendarDTO, rampId: string) => {
      console.log('applySlot', { slotTimezoneId: slot.timezoneId, rampId });
      form.setFieldValue(form.names.arrivalHourString, stringHHmmFromStringInTz(slot.utcStart, slot.timezoneId));
      form.setFieldValue(form.names.rampId, rampId);
      form.setFieldValue('timezoneId', slot.timezoneId);
      setSlots(undefined);
    };

    const [reservationValid, setReservationValid] = useState(undefined);
    const [reservationCheckMessages, setReservationCheckMessages] = useState<MessageResponse[]>([]);

    useEffect(() => {
      const ab = new AbortController();
      if (form.values?.rampId && form.values?.durationInMin && form.values?.arrivalHourString && form.values?.localDate) {
        setStateForDeferred({
          rampId: form.values.rampId,
          durationInMin: form.values.durationInMin,
          arrivalHourString: form.values.arrivalHourString,
          localDate: form.values.localDate,
          timezoneId: form.values.timezoneId,
        });
      }

      setReservationValid(undefined);
      return () => {
        ab.abort();
      };
    }, [form.values?.arrivalHourString, form.values?.durationInMin, form.values?.rampId, form.values?.localDate, form.values?.timezoneId]);

    const [stateForDeferred, setStateForDeferred] = useState(undefined);
    const [debouncedValue] = useDebounce(stateForDeferred, 700);

    useEffect(() => {
      checkReservationHandler();
    }, [debouncedValue]);

    const checkReservationHandler = () => {
      if (!deepEqual(debouncedValue, originalValues)) {
        if (isNotNullOrUndefined(debouncedValue)) {
          setApiRequest(true);
          checkReservation(
            [reservationId],
            {
              customerId: companyLocationCustomerId,
              timeslot: editSlotTimeslotForReservationCheck(debouncedValue),
              rampId: debouncedValue.rampId,
              locationId: form.values.companyLocationId,
              labelIds: [],
              linkToExpeditionLocationId: linkedExpeditionLocationId,
              openGate: false,
              createExpedition: false,
              sendNotification: true,
            },
            () => {
              setApiRequest(false);
              setReservationValid(true);
              setReservationCheckMessages([]);
            },
            null,
            {
              onError: e => {
                setApiRequest(false);
                setReservationValid(false);
                // Unknown/transport error — no messages, the generic wording is shown
                setReservationCheckMessages([]);
              },
              onValidationFailed: messages => {
                setApiRequest(false);
                setReservationValid(false);
                setReservationCheckMessages(messages);
              },
            },
          );
        }
      } else {
        setReservationValid(true);
        setReservationCheckMessages([]);
      }
    };

    const isOwner = loggedUser?.companyId == reservationCompanyOwnerId;
    const isEditDurationDisabled = isOwner ? settings?.editDuration == false : settings?.editDurationCounterParty == false;

    const { externalOrdersMinDate, externalOrdersMaxDate } = useExternalOrdersDateRange(orders);

    useEffect(() => {
      if (!externalOrdersMinDate) return;
      const current = form.values?.localDate;
      if (!current) return;
      const minStr = stringYMDFromDate(externalOrdersMinDate);
      const maxStr = externalOrdersMaxDate ? stringYMDFromDate(externalOrdersMaxDate) : null;
      if (current < minStr || (maxStr && current > maxStr)) {
        form.setFieldValue(form.names.localDate, minStr);
      }
    }, [externalOrdersMinDate, externalOrdersMaxDate]);

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          heading={tr('EditReservationSlot.editReservationSlot', 'Edit reservation slot')}
          onContinue={() => {
            form.validateAndSend();
          }}
          loading={saving}
        >
          {isNotNullOrUndefined(form.values) && (
            <>
              <BackendValidationComponent />
              <ColWithGap>
                <VerticalFieldAndValueWithIcon
                  label={tr(`EditReservationSlotSidebar.forReservation`, `For reservation`)}
                  valueText={applicationId}
                  iconPath={'/images/icons/ringil3/forExpedition.svg'}
                />

                <HorizontalLine variant={'tertiary'} />
                <VerticalFieldAndValue
                  label={tr('EditReservationSlot.location', 'Location')}
                  labelMode={'normal'}
                  readOnly={false}
                  required={true}
                  errorMessage={findValidationMessage(form.validationErrors, form.names.companyLocationId, tr)}
                >
                  <InputCompanyLocation
                    companyId={companyLocationCustomerId}
                    companyLocationId={form.values.companyLocationId}
                    onChange={v => {
                      form.setValues({
                        companyLocationId: v?.id,
                        rampId: null,
                        timezoneId: null, // Clear timezoneId to reload from new location
                      });
                    }}
                    moduleName={CompanyModuleEnum.TIMESLOTS}
                    disabled={false}
                    showOnlyWithRamp={true}
                    filterEnabled={true}
                  />
                </VerticalFieldAndValue>
                <HorizontalLine variant={'tertiary'} />
                <COHeadline variant={'h3'} title={tr(`EditReservationSlotSidebar.plan`, `Plan`)}></COHeadline>
                <VerticalFieldAndValue
                  label={tr(`EditReservationSlotSidebar.localDate`, `Date`)}
                  labelMode={'normal'}
                  required={true}
                  errorMessage={findValidationMessage(form.validationErrors, form.names.localDate, tr)}
                >
                  <RingilCalendar
                    value={form.values.localDate}
                    onChange={v => form.setFieldValue(form.names.localDate, v)}
                    fullWidth
                    minDate={externalOrdersMinDate}
                    maxDate={externalOrdersMaxDate}
                  />
                </VerticalFieldAndValue>
                {!slots && (<LinkWithIcon
                    icon={<SvgAvailableDates />}
                    label={tr(`CreateTimeslotSidebar.offerAvailableSlots`, `Offer available slots`)}
                    onClick={() => {
                      offerAvailableSlots();
                    }}
                  />
                )}

                {slots && (
                  <Panel onClose={() => setSlots(undefined)}
                         headingText={tr('CreateTimeslotSidebar.availableSlots', 'Available slots')}
                         closeIcon={<SvgClose />}>
                    <ColWithGap>
                      {!slots[0]?.freeSlots?.length ? (
                        <NoAvailableSlotsMessage />
                      ) : (
                        slots[0].freeSlots.map((s, i) => {
                        return (
                          <VerticalFieldAndValue label={s.name} labelMode={'normal'} key={i}>
                            {s.slots.length == 0 && (
                              <NoAvailableSlotsMessage unavailability={s.unavailability} />
                            )}
                            <SlotsGrid>
                              {s.slots.map((slot, j) => {
                                return (
                                  <ClickableCursor onClick={() => applySlot(slot, s.rampId)}>
                                    <COSelect text={`${stringHHmmFromStringInTz(slot.utcStart, slot.timezoneId)} - ${stringHHmmFromStringInTz(slot.utcEnd, slot.timezoneId)}`}
                                              key={j} />
                                  </ClickableCursor>
                                );
                              })}
                            </SlotsGrid>
                          </VerticalFieldAndValue>
                        );
                        })
                      )}
                    </ColWithGap>
                  </Panel>
                )}
                <TwoColGrid>
                  <VerticalFieldAndValue
                    label={tr(`EditReservationSlotSidebar.ramp`, `Ramp`)}
                    labelMode={'normal'}
                    required={true}
                    errorMessage={findValidationMessage(form.validationErrors, form.names.rampId, tr)}
                  >
                    <InputCompanyRamp
                      value={form.values.rampId}
                      onChange={v => form.setFieldValue(form.names.rampId, v)}
                      companyLocationId={form.values.companyLocationId}
                      companyId={companyLocationCustomerId}
                    />
                  </VerticalFieldAndValue>
                  <VerticalFieldAndValue
                    label={tr(`EditReservationSlotSidebar.duration`, `Duration`)}
                    labelMode={'normal'}
                    required={true}
                    errorMessage={findValidationMessage(form.validationErrors, form.names.durationInMin, tr)}
                  >
                    <InputTextWithSuffix
                      suffix={'min'}
                      value={form.values.durationInMin}
                      onChange={v => form.setFieldValue(form.names.durationInMin, parseNumberOrUndefined(v))}
                      readOnly={isEditDurationDisabled}
                    />
                  </VerticalFieldAndValue>
                </TwoColGrid>

                <VerticalFieldAndValue
                  label={tr(`EditReservationSlotSidebar.arrival`, `Arrival`)}
                  labelMode={'normal'}
                  required={true}
                  errorMessage={findValidationMessage(form.validationErrors, form.names.arrivalHourString, tr)}
                >
                  <InputHourManual value={form.values.arrivalHourString}
                                   onChange={v => form.setFieldValue(form.names.arrivalHourString, v)} />
                </VerticalFieldAndValue>

                <RowWithGap>
                  <LinkWithIcon
                    icon={<SvgRefresh />}
                    label={tr(`CreateTimeslotSidebar.checkReservation`, `Check reservation`)}
                    onClick={() => {
                      checkReservationHandler();
                    }}
                  />

                  {apiRequest && (
                    <>
                      <ProgressSpinner
                        style={{
                          width: '15px',
                          height: '15px',
                          marginRight: '0.5rem',
                          color: 'var(--ringil3-brand-color)',
                        }}
                        strokeWidth="3"
                        animationDuration="1s"
                      />
                    </>
                  )}
                </RowWithGap>
                <ReservationCheckResult valid={reservationValid} messages={reservationCheckMessages} />
              </ColWithGap>
            </>
          )}
        </RingilSidebar>
      </>
    );
  };

  return { EditReservationSlotSidebar, setVisible, visible };
};

const SlotsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
`;
